import React, { useState, useEffect } from "react";
import { DatabaseActions } from "./DatabaseActions";
import { consigneeService, Consignee } from "../Services/ConsigneeService"
import countryCodes from "../data/countryCodes.json";
import toast from "react-hot-toast";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { useDeleteConfirmation } from "../hooks/useDeleteConfirmation";

interface LimitInfo {
  canAdd: boolean;
  currentCount: number;
  limit: number | string;
  tier: string;
  wouldExceedBy?: number;
}

interface ServerError {
  message: string;
  limitInfo?: LimitInfo;
}

export const ConsigneeDatabase: React.FC = () => {
  const [consignees, setConsignees] = useState<Consignee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedConsignee, setSelectedConsignee] = useState<Consignee | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortField, setSortField] = useState<keyof Consignee>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [formData, setFormData] = useState<Partial<Consignee>>({
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    phoneNumber: "",
    email: "",
    taxId: "",
    consigneeReference: "",
    notes: ""
  });

  // Use the delete confirmation hook
  const {
    showDeleteModal,
    itemToDelete,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    getItemName
  } = useDeleteConfirmation({
    items: consignees,
    setItems: setConsignees,
    deleteFunction: consigneeService.deleteConsignee,
    getItemName: (consignee: Consignee) => consignee.name,
    getItemId: (consignee: Consignee) => consignee._id || '',
    itemType: 'Consignee'
  });

  const countryOptions = Object.entries(countryCodes).map(([name, code]) => ({
    name,
    code
  }));

  const showLimitError = (limitInfo: LimitInfo) => {
    const limitText = limitInfo.limit === 'unlimited' ? 'unlimited' : limitInfo.limit.toString();

    toast.error(
      <div className="space-y-2">
        <div className="font-medium">Consignee Limit Exceeded</div>
        <div className="text-sm">
          Your <span className="capitalize font-medium">{limitInfo.tier}</span> plan allows {limitText} consignees.
          <br />
          You currently have {limitInfo.currentCount} consignees.
        </div>
        <button
          onClick={() => {
            // Navigate to upgrade page or show upgrade modal
            window.location.href = '/account#subscription';
          }}
          className="mt-2 px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
        >
          Upgrade Plan
        </button>
      </div>,
      {
        duration: 10000,
        style: {
          maxWidth: '400px',
        }
      }
    );
  };

  const fetchConsignees = async () => {
    setLoading(true);
    try {
      const data = await consigneeService.getAllConsignees();
      setConsignees(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching consignees:", err);
      setError("Failed to load consignees. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsignees();
  }, []);

  // Function to handle sorting
  const handleSort = (field: keyof Consignee) => {
    if (field === sortField) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as sort field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Function to get sorted consignees
  const getSortedConsignees = () => {
    // Create a copy of consignees to avoid mutating state directly
    const sortedConsignees = [...consignees];

    return sortedConsignees.sort((a, b) => {
      // Handle undefined values
      const valueA = a[sortField] || "";
      const valueB = b[sortField] || "";

      // For alphanumeric sorting of consignee names
      if (sortField === "name") {
        // Natural sort algorithm for alphanumeric strings
        return sortDirection === "asc"
          ? valueA.toString().localeCompare(valueB.toString(), undefined, { numeric: true, sensitivity: 'base' })
          : valueB.toString().localeCompare(valueA.toString(), undefined, { numeric: true, sensitivity: 'base' });
      }

      // For other fields, use standard string comparison
      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleAddConsignee = () => {
    setSelectedConsignee(null);
    setFormData({
      name: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      phoneNumber: "",
      email: "",
      taxId: "",
      consigneeReference: "",
      notes: ""
    });
    setShowModal(true);
  };

  const handleEditConsignee = (consignee: Consignee) => {
    setSelectedConsignee(consignee);
    setFormData({ ...consignee });
    setShowModal(true);
  };

  const handleDeleteConsignee = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this consignee?")) return;

    try {
      await consigneeService.deleteConsignee(id);
      setConsignees(consignees.filter(consignee => consignee._id !== id));
    } catch (err) {
      console.error("Error deleting consignee:", err);
      alert("Failed to delete consignee. Please try again.");
    }
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as ###-###-####
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Special handling for phone number
    if (name === 'phoneNumber') {
      // Format the phone number
      const formattedValue = formatPhoneNumber(value);
      setFormData((prev: Partial<Consignee>) => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      // Normal handling for other fields
      setFormData((prev: Partial<Consignee>) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedConsignee && selectedConsignee._id) {
        // Update existing consignee
        const updatedConsignee = await consigneeService.updateConsignee(selectedConsignee._id, formData as Consignee);
        setConsignees(consignees.map(c => c._id === selectedConsignee._id ? updatedConsignee : c));
        toast.success("Consignee updated successfully");
      } else {
        // Create new consignee
        const newConsignee = await consigneeService.createConsignee(formData as Consignee);
        setConsignees([...consignees, newConsignee]);
        toast.success("Consignee added successfully");
      }
      setShowModal(false);
    } catch (err: any) {
      console.error("Error saving consignee:", err);

      // Handle different types of errors
      if (err.response?.status === 403) {
        // Subscription limit error
        const serverError: ServerError = err.response.data;
        if (serverError.limitInfo) {
          showLimitError(serverError.limitInfo);
        } else {
          toast.error(serverError.message || "You've reached your subscription limit");
        }
      } else if (err.response?.status === 400) {
        // Validation error
        const errorMessage = err.response.data.message || "Invalid data provided";
        toast.error(errorMessage);
      } else {
        // Generic error
        toast.error("Failed to save consignee. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the sorted consignees
  const sortedConsignees = getSortedConsignees();

  // Helper function to render sort indicator
  const renderSortIndicator = (field: keyof Consignee) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="p-4">
      <h3 className="text-xl font-semibold mb-3">Consignees Database</h3>
      <DatabaseActions databaseType="consignees" onRefresh={fetchConsignees} />

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium">Consignees List</h4>
          <button
            onClick={handleAddConsignee}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            Add New Consignee
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">Loading consignees...</div>
        ) : consignees.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No consignees found. Add a new consignee or upload consignees from a file.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("name")}
                  >
                    Consignee Name {renderSortIndicator("name")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("country")}
                  >
                    Location {renderSortIndicator("country")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("phoneNumber")}
                  >
                    Phone {renderSortIndicator("phoneNumber")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("email")}
                  >
                    Email {renderSortIndicator("email")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider select-none">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedConsignees.map((consignee) => (
                  <tr key={consignee._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {consignee.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {`${consignee.city}, ${consignee.country}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {consignee.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {consignee.email || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditConsignee(consignee)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(consignee)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={getItemName}
        itemType="Consignee"
        isDeleting={isDeleting}
      />

      {/* Add/Edit Consignee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">
                  {selectedConsignee ? "Edit Consignee" : "Add New Consignee"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="consigneeName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Consignee Name *
                    </label>
                    <input
                      type="text"
                      id="consigneeName"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        City *
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        State/Province *
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Zip/Postal Code *
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Country *
                      </label>
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">Select a country...</option>
                        {countryOptions.sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name} ({country.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                      placeholder="123-456-7890"
                      maxLength={12}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>


                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tax ID
                    </label>
                    <input
                      type="text"
                      id="taxId"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="consigneeReference" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Consignee Reference
                    </label>
                    <input
                      type="text"
                      id="consigneeReference"
                      name="consigneeReference"
                      value={formData.consigneeReference}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting
                      ? (selectedConsignee ? "Updating..." : "Adding...")
                      : (selectedConsignee ? "Update Consignee" : "Add Consignee")
                    }
                  </button>

                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

